import './ProjectDetail.scss';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react'
import { useEffect } from 'react';
import { useState } from 'react';
import { Alert, Badge, Breadcrumb, Button, Card, Col, Container, Modal, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Projects } from '../../api/Projects';
import AppLoader from '../../components/common/AppLoader';
import UserItem from '../../components/common/UserItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { formatDate } from '../../utils';
import { ProjectAsset } from '../../components/list-items/ProjectAsset';
import { Tooltip } from 'antd';
import { Project } from '../../models/Project';


export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [project, setProject] = useState<Project>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    Projects.getProject(+id!)
      .then(
        ({ data: newProject }) => setProject(newProject),
        () => {
          showToast('project-error', 'Could not get project.', 'Project Error', 'danger');
          setError(true);
        }
      );
    // eslint-disable-next-line
  }, []);

  const onEditProject = () => navigate(`/projects/${id}/edit`);
  const onDeleteProject = () => setShowDeleteModal(true);
  const handleDeleteClose = () => setShowDeleteModal(false);

  const deleteProject = () => {
    showLoader();

    Projects.deleteProject(project!.id!)
      .then(
        () => {
          showToast('project-delete-success', 'Project deleted successfully.', 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/projects');
        },
        () => {
          showToast('project-delete-error', 'Could not delete project.', 'Project Error', 'danger');
          hideLoader();
        }
      );
  };

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured during project retrieve. Try again later.</span>
      </Alert>
    );

    if (!project) return (
      <div className="d-flex justify-content-center py-5">
        <AppLoader size={300} />
      </div>
    );

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/projects' }}>Projects</Breadcrumb.Item>
          <Breadcrumb.Item active>{project.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card className="project-detail-group main bg-project">
          <FontAwesomeIcon className="project-icon" icon={solid('folder')} />

          <div className="position-relative">
            <Row className="gx-0 justify-content-end">
              <Badge className="w-auto shadow">
                {project.accessLevel}
              </Badge>
            </Row>

            <div className="p-2">
              <Row className="gx-0 align-items-center">
                <Col className="pe-3">
                  <h3 className="mb-0">{project.name}</h3>
                  <p className="text-trailing mb-3">{project.description}</p>
                </Col>

                <div className="w-auto">
                  <Tooltip title="Edit">
                    <button className="circle-button p-4 ms-3" onClick={onEditProject}>
                      <FontAwesomeIcon icon={solid('pen')} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <button className="circle-button p-4 ms-3" onClick={onDeleteProject}>
                      <FontAwesomeIcon icon={solid('trash')} />
                    </button>
                  </Tooltip>
                </div>
              </Row>
            </div>

            <Row className="gx-0 justify-content-between">
              <Col xs="auto">
                <div className="bg-highlight">
                  <UserItem user={project.owner!} organization={project.organization!} />
                </div>
              </Col>

              <div className="d-flex flex-wrap">
                {
                  project.tags?.map((tag) => <Badge className="m-1" pill={true}>{tag}</Badge>)
                }
              </div>
            </Row>

            <div className="project-dates">
              <div className="d-inline-block">
                <b>Created:</b>
                <span className="ms-1">{formatDate(project.createDateTime!)}</span>
              </div>
              <div className="d-inline-block ms-2">
                <b>Last Update:</b>
                <span className="ms-1">{formatDate(project.updateDateTime!)}</span>
              </div>
            </div>
          </div>
        </Card>

        <Card className="project-detail-group mb-3">
          <div className="project-detail-highlight">
            <FontAwesomeIcon icon={solid('shapes')} />
            <h5 className="d-inline ms-2 mb-0">Components</h5>
          </div>

          <Card.Body>
            <Row>
              {project.bdaApplications.map(app => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={app.id}>
                  <ProjectAsset asset={app} type="application" />
                </Col>
              ))}
              {project.datasets.map(dataset => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={dataset.id}>
                  <ProjectAsset asset={dataset} type="dataset" />
                </Col>
              ))}
              {project.models.map(model => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={model.id}>
                  <ProjectAsset asset={model} type="model" />
                </Col>
              ))}
              {project.services.map(service => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={service.id}>
                  <ProjectAsset asset={service} type="service" />
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Project {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Project Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete project <b>{project?.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteProject}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}